import type { ReactElement } from 'react';

import { ActivityDetailDrawerExamples } from './examples/ActivityDetailDrawerExamples';
import { ActivityTableExamples } from './examples/ActivityTableExamples';
import { ButtonExamples } from './examples/ButtonExamples';
import { CollapsibleCardExamples } from './examples/CollapsibleCardExamples';
import { ConfirmDialogExamples } from './examples/ConfirmDialogExamples';
import { ContactFieldsExamples } from './examples/ContactFieldsExamples';
import { DataTableExamples } from './examples/DataTableExamples';
import { DialogExamples } from './examples/DialogExamples';
import { FieldExamples } from './examples/FieldExamples';
import { FilterBarExamples } from './examples/FilterBarExamples';
import { GeographicScopeFieldsExamples } from './examples/GeographicScopeFieldsExamples';
import { ImageUploadExamples } from './examples/ImageUploadExamples';
import { LiveRegionExamples } from './examples/LiveRegionExamples';
import { PageHeaderExamples } from './examples/PageHeaderExamples';
import { PasswordFieldExamples } from './examples/PasswordFieldExamples';
import { RowActionsMenuExamples } from './examples/RowActionsMenuExamples';
import { SkeletonExamples } from './examples/SkeletonExamples';
import { SocialLinksEditorExamples } from './examples/SocialLinksEditorExamples';
import { StatCardExamples } from './examples/StatCardExamples';
import { StatusPageExamples } from './examples/StatusPageExamples';
import { ToastExamples } from './examples/ToastExamples';
import { UnsavedChangesDialogExamples } from './examples/UnsavedChangesDialogExamples';

/**
 * The registry the gallery renders, one entry per shared component, in
 * alphabetical order.
 *
 * The completeness test in entries.test.tsx reads the shared folders' exports
 * and fails naming any component missing from here - so a component can be
 * added to the portal, but not quietly kept out of the gallery.
 */
export interface GalleryEntryDefinition {
  /** The heading's id, which the contents list links to. */
  id: string;
  /** The component's exported name, which is also the heading. */
  name: string;
  /** Further exported names this entry documents ("StatCardRow"). */
  covers?: string[];
  /** One line: what it is for. */
  purpose: string;
  /** Where it lives. */
  file: string;
  /** The screens that use it. */
  usedBy: string[];
  render: () => ReactElement;
}

export const GALLERY_ENTRIES: GalleryEntryDefinition[] = [
  {
    id: 'activity-detail-drawer',
    name: 'ActivityDetailDrawer',
    purpose: 'One audit entry in full: who, when, what, and what changed.',
    file: 'src/components/activity/ActivityDetailDrawer.tsx',
    usedBy: ['Activity', 'Staff activity'],
    render: () => <ActivityDetailDrawerExamples />,
  },
  {
    id: 'activity-table',
    name: 'ActivityTable',
    purpose: 'The audit log as a filtered, paged table with a drawer for each entry.',
    file: 'src/components/activity/ActivityTable.tsx',
    usedBy: ['Activity', 'Staff activity'],
    render: () => <ActivityTableExamples />,
  },
  {
    id: 'button',
    name: 'Button',
    purpose:
      'Every button in the portal: four intents, two sizes, loading and link forms.',
    file: 'src/components/ui/Button.tsx',
    usedBy: ['Every screen'],
    render: () => <ButtonExamples />,
  },
  {
    id: 'collapsible-card',
    name: 'CollapsibleCard',
    purpose: 'One section of a long page, behind a header that folds away.',
    file: 'src/components/ui/CollapsibleCard.tsx',
    usedBy: ['Staff organization page'],
    render: () => <CollapsibleCardExamples />,
  },
  {
    id: 'confirm-dialog',
    name: 'ConfirmDialog',
    purpose: 'A yes-or-no question asked before something hard to undo.',
    file: 'src/components/ui/ConfirmDialog.tsx',
    usedBy: ['Users', 'Profile', 'Staff organization page', 'every form’s leave prompt'],
    render: () => <ConfirmDialogExamples />,
  },
  {
    id: 'contact-fields',
    name: 'ContactFields',
    purpose: 'The four parts of a contact, labelled per form by a prefix.',
    file: 'src/components/form/ContactFields.tsx',
    usedBy: ['Profile', 'Billing', 'Staff organization forms'],
    render: () => <ContactFieldsExamples />,
  },
  {
    id: 'data-table',
    name: 'DataTable',
    purpose: 'The table every list page uses; the server sorts and pages, it renders.',
    file: 'src/components/ui/DataTable.tsx',
    usedBy: ['Users', 'Staff organizations', 'Activity'],
    render: () => <DataTableExamples />,
  },
  {
    id: 'dialog',
    name: 'Dialog',
    purpose: 'The modal every dialog builds on: focus, Escape and the phone layout.',
    file: 'src/components/ui/Dialog.tsx',
    usedBy: ['Invite user', 'Change role', 'every confirmation'],
    render: () => <DialogExamples />,
  },
  {
    id: 'field',
    name: 'Field',
    purpose: 'One form control with its label, hint and announced error message.',
    file: 'src/components/ui/Field.tsx',
    usedBy: ['Every form'],
    render: () => <FieldExamples />,
  },
  {
    id: 'filter-bar',
    name: 'FilterBar',
    purpose: 'Search, dropdowns and a date range above a list, with one Clear.',
    file: 'src/components/ui/FilterBar.tsx',
    usedBy: ['Staff organizations', 'Activity'],
    render: () => <FilterBarExamples />,
  },
  {
    id: 'geographic-scope-fields',
    name: 'GeographicScopeFields',
    purpose: 'Where an organization works: a scope, its states, and a note.',
    file: 'src/components/form/GeographicScopeFields.tsx',
    usedBy: ['Profile', 'Staff organization forms'],
    render: () => <GeographicScopeFieldsExamples />,
  },
  {
    id: 'image-upload',
    name: 'ImageUpload',
    purpose: 'One image: what is stored, a way to replace it, a way to remove it.',
    file: 'src/components/form/ImageUpload.tsx',
    usedBy: ['Profile'],
    render: () => <ImageUploadExamples />,
  },
  {
    id: 'live-region',
    name: 'LiveRegion',
    purpose: 'Announces a change to screen-reader users without moving focus.',
    file: 'src/components/ui/LiveRegion.tsx',
    usedBy: ['DataTable', 'the skeletons', 'ImageUpload'],
    render: () => <LiveRegionExamples />,
  },
  {
    id: 'page-header',
    name: 'PageHeader',
    purpose: 'The title block every page begins with; it also names the browser tab.',
    file: 'src/components/ui/PageHeader.tsx',
    usedBy: ['Every screen'],
    render: () => <PageHeaderExamples />,
  },
  {
    id: 'password-field',
    name: 'PasswordField',
    purpose: 'A password box with a real button that shows what was typed.',
    file: 'src/components/ui/PasswordField.tsx',
    usedBy: ['Sign in', 'Reset password', 'Accept invitation'],
    render: () => <PasswordFieldExamples />,
  },
  {
    id: 'row-actions-menu',
    name: 'RowActionsMenu',
    purpose: 'The actions for one row of a table, behind one named control.',
    file: 'src/components/ui/RowActionsMenu.tsx',
    usedBy: ['Users', 'Staff organizations'],
    render: () => <RowActionsMenuExamples />,
  },
  {
    id: 'skeletons',
    name: 'Skeletons',
    covers: ['FormSkeleton', 'SectionSkeleton', 'CardSkeleton'],
    purpose: 'Placeholders the shape of a page that is still loading, so nothing jumps.',
    file: 'src/components/ui/Skeletons.tsx',
    usedBy: ['Profile', 'Billing', 'Staff organization page'],
    render: () => <SkeletonExamples />,
  },
  {
    id: 'social-links-editor',
    name: 'SocialLinksEditor',
    purpose: 'An organization’s social links, as rows added and removed.',
    file: 'src/components/form/SocialLinksEditor.tsx',
    usedBy: ['Profile', 'Staff organization forms'],
    render: () => <SocialLinksEditorExamples />,
  },
  {
    id: 'stat-card',
    name: 'StatCard',
    covers: ['StatCardRow'],
    purpose: 'One summary number, and the row that lays several out responsively.',
    file: 'src/components/ui/StatCard.tsx',
    usedBy: ['Dashboard'],
    render: () => <StatCardExamples />,
  },
  {
    id: 'status-page',
    name: 'StatusPage',
    purpose: 'One shape for no access, not found, and a page that failed.',
    file: 'src/features/errors/StatusPage.tsx',
    usedBy: ['The 403 page', 'the 404 page'],
    render: () => <StatusPageExamples />,
  },
  {
    id: 'toast',
    name: 'Toast',
    covers: ['ToastProvider'],
    purpose: 'Short confirmations after an action, one at a time, via useToast().',
    file: 'src/components/ui/Toast.tsx',
    usedBy: ['Every save'],
    render: () => <ToastExamples />,
  },
  {
    id: 'unsaved-changes-dialog',
    name: 'UnsavedChangesDialog',
    purpose: 'The one wording for “you are about to lose your edits”.',
    file: 'src/components/form/UnsavedChangesDialog.tsx',
    usedBy: ['Every form that tracks unsaved edits'],
    render: () => <UnsavedChangesDialogExamples />,
  },
];
